export type ProjectId = "tracker" | "counter" | "spotter";
export interface Feature {
  label: string;
  title: string;
  description: string;
}
export interface Project {
  id: ProjectId;
  name: string;
  category: string;
  tagline: string;
  summary: string;
  icon: string;
  technologies: string[];
  features: Feature[];
  details: { title: string; text: string }[];
  // Add approved product captures here; never substitute fictional UI.
  screenshots: { src: string; alt: string; caption: string }[];
}
export const projects: Project[] = [
  {
    id: "counter",
    name: "Counter",
    category: "Everyday nutrition",
    tagline: "Less friction.\nMore perspective.",
    summary:
      "Log what you eat, plan what comes next, and see your nutrition clearly. Built around real portions and everyday meals.",
    icon: "/images/counter.png",
    technologies: ["Swift", "SwiftUI", "WidgetKit", "Open Food Facts"],
    screenshots: [
      {
        src: "/images/counter-widget.png",
        alt: "Counter nutrition widget showing calories and protein, carbohydrate, and fat goals with demonstration values.",
        caption: "Nutrition widget · development capture · sample data",
      },
      {
        src: "/images/counter-portion.png",
        alt: "Counter food logging screen with a named tablespoon portion and a nutrition preview before logging.",
        caption: "Portion entry · development capture · sample data",
      },
    ],
    features: [
      {
        label: "Portion-aware logging",
        title: "A portion that\nmakes sense.",
        description:
          "Use measured portions and preview calories and macros before logging. Save familiar portions for next time.",
      },
      {
        label: "Home Screen widgets",
        title: "Your day.\nAt a glance.",
        description:
          "Calories, macro goals, and shortcuts to logging. Available in three widget sizes.",
      },
      {
        label: "Plans ≠ intake",
        title: "Plan ahead.\nLog when ready.",
        description:
          "Planned meals stay separate from consumed totals. Review and edit before they become part of your day.",
      },
    ],
    details: [
      {
        title: "The problem",
        text: "A package, a portion, and a meal are not the same measurement. Logging needs to make that distinction clear before it changes daily totals.",
      },
      {
        title: "The approach",
        text: "Keep the nutrition reference amount separate from the amount eaten. Barcode lookup checks the saved library, then Open Food Facts; incomplete labels fall back to manual entry.",
      },
      {
        title: "Under the surface",
        text: "A shared daily nutrition snapshot keeps app and widgets consistent. Meal plans track the foods they own, so undoing a plan does not remove unrelated intake.",
      },
      {
        title: "A deliberate boundary",
        text: "Scanning asks for a portion and confirmation before logging. Widget refresh timing is controlled by iOS. The images shown are development captures, not a claim of a released version.",
      },
    ],
  },
  {
    id: "spotter",
    name: "Spotter",
    category: "Training & progress",
    tagline: "Every set.\nA step forward.",
    summary:
      "Plan your training, log your sets, and build on your history. A focused workout companion for iPhone and Apple Watch.",
    icon: "/images/spotter.png",
    technologies: ["SwiftUI", "Core Data", "ActivityKit", "WatchConnectivity"],
    screenshots: [],
    features: [
      {
        label: "Progression with context",
        title: "Your history.\nYour next step.",
        description:
          "Local rules turn completed workouts into explained progression suggestions. You choose when to apply them.",
      },
      {
        label: "Apple Watch",
        title: "Keep your\nworkout moving.",
        description:
          "Download a workout from iPhone, then log, adjust, rest, and finish on Watch—even while disconnected.",
      },
      {
        label: "Live Activities",
        title: "Less unlocking.\nMore training.",
        description:
          "See your current exercise and rest timer. Log a set or end rest from the Lock Screen and expanded Dynamic Island.",
      },
    ],
    details: [
      {
        title: "The problem",
        text: "Workout planning, active logging, and completed history have different jobs. Moving between them should preserve what actually happened.",
      },
      {
        title: "The approach",
        text: "Separate routines, scheduled sessions, active workouts, and history. Create new identities when repeating a workout, and keep completed sets when swapping an exercise.",
      },
      {
        title: "Under the surface",
        text: "Core Data owns the workout record. Watch changes use durable local storage, stable operation IDs, acknowledgements, and duplicate protection. Live Activities reflect the saved session.",
      },
      {
        title: "A deliberate boundary",
        text: "Start or download a workout on iPhone before independent Watch use. Progression uses local rules, not a learned AI model. Physical-device lifecycle and accessibility checks remain ongoing.",
      },
    ],
  },
  {
    id: "tracker",
    name: "Tracker",
    category: "Personal finance",
    tagline: "A clearer picture.\nA better plan.",
    summary:
      "Accounts, budgets, bills, and savings goals. A private place to make sense of your money, on iPhone and iPad.",
    icon: "/images/tracker.png",
    technologies: ["Swift", "SwiftUI", "Decimal", "Protected local storage"],
    screenshots: [],
    features: [
      {
        label: "The everyday overview",
        title: "See what’s left.\nKnow what’s next.",
        description:
          "Monthly budget remaining, cash flow, upcoming bills, and recent activity in one overview.",
      },
      {
        label: "Built around your plan",
        title: "Give your money\na direction.",
        description:
          "Set category budgets, manage recurring bills, and record progress toward savings goals.",
      },
      {
        label: "On your terms",
        title: "Your data.\nOn your device.",
        description:
          "Local storage with file protection. Export transactions as CSV or make a complete JSON backup.",
      },
    ],
    details: [
      {
        title: "The problem",
        text: "Balances alone don’t explain what is available to spend. Budgets, upcoming bills, and transfers need distinct meanings.",
      },
      {
        title: "The approach",
        text: "Separate recorded activity from planning. Transfers update two accounts without appearing as spending or income; savings progress is not counted twice in net worth.",
      },
      {
        title: "Under the surface",
        text: "Decimal amounts preserve money calculations. Validated CSV imports preview errors and suppress duplicate rows. Atomic file writes protect the saved document.",
      },
      {
        title: "A deliberate boundary",
        text: "This version uses manual entry and file import. It does not connect to banks or sync to the cloud. Combined totals use the selected base currency.",
      },
    ],
  },
];
export const professionalLinks = [
  { label: "LinkedIn", href: "https://www.linkedin.com/in/kevinlhn/" },
  { label: "GitHub", href: "https://github.com/Kevinlhn" },
];
